import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './RecentActivity.css';

const formatLookupReason = reason => reason === 'country_required'
  ? 'Country is missing or unrecognized. Fill it in, then change the postal code.'
  : reason;

function RecentActivity() {
  const { user } = useAuth();
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);

  const fetchRecentLookups = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/recent-lookups`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        params: {
          limit: 50
        }
      });
      setLookups(response.data.lookups);
      setError('');
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch recent lookups:', error);
      setError('Failed to load recent activity');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.token) {
      fetchRecentLookups();
    }
  }, [user]);

  useEffect(() => {
    if (user && user.token) {
      // Auto-refresh every 30 seconds
      intervalRef.current = setInterval(() => {
        fetchRecentLookups();
      }, 30000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [user]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) {
      return 'Just now';
    }
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    
    // More than 24 hours - show date
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (successful, reason) => {
    if (successful) {
      return <span className="status-badge status-success">Success</span>;
    } else {
      return (
        <span className="status-badge status-failed" title={formatLookupReason(reason) || 'Unknown error'}>
          Failed
        </span>
      );
    }
  };

  const formatUpdatedData = (updatedDataJson) => {
    if (!updatedDataJson) return null;
    
    try {
      const data = typeof updatedDataJson === 'string' ? JSON.parse(updatedDataJson) : updatedDataJson;
      const entries = Object.entries(data);
      
      if (entries.length === 0) return null;
      
      // Create a readable list of what was updated
      const formattedValues = entries
        .filter(([key, value]) => value) // Filter out null/empty values
        .map(([key, value]) => value);
      
      if (formattedValues.length === 0) return null;
      
      return formattedValues.join(', ');
    } catch (err) {
      console.error('Error parsing updated data:', err);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-card recent-activity-card">
        <div className="card-header">
          <h2>Recent Activity</h2>
        </div>
        <div className="recent-activity-loading">
          <div className="loading-spinner"></div>
          <p>Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-card recent-activity-card">
        <div className="card-header">
          <h2>Recent Activity</h2>
        </div>
        <div className="recent-activity-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card recent-activity-card">
      <div className="card-header">
        <h2>Last 50 Lookups</h2>
        <div className="activity-controls">
          <button 
            onClick={fetchRecentLookups} 
            className="btn-icon"
            title="Refresh"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
              <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>
        </div>
      </div>
      
      {lookups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <p>No lookup activity yet</p>
          <p className="empty-hint">Your recent zipcode lookups will appear here.</p>
        </div>
      ) : (
        <>
          <div className="activity-stats">
            <span className="stat-item">
              <span className="stat-label">Successful:</span> 
              <span className="stat-value stat-success">{lookups.filter(l => l.successful).length}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Failed:</span> 
              <span className="stat-value stat-failed">{lookups.filter(l => !l.successful).length}</span>
            </span>
          </div>
          
          <div className="recent-activity-list">
            {lookups.map((lookup) => (
              <div key={lookup.id} className="activity-item">
                <div className="activity-content">
                  <span className="activity-zipcode">{lookup.zipcode}</span>
                  <span className="activity-separator">·</span>
                  {lookup.country_code && <>
                    <span title="Lookup country">{lookup.country_code}</span>
                    <span className="activity-separator">·</span>
                  </>}
                  <span className="object-type-badge">
                    {lookup.lookup_mode === 'global' ? 'Global (experimental)' : lookup.lookup_mode === 'legacy_fallback' ? 'US/Canada fallback' : 'Standard'}
                  </span>
                  <span className="activity-separator">·</span>
                  <span>{!lookup.lookup_mode && lookup.successful ? 1 : Number(lookup.credits_charged || 0)} credits</span>
                  <span className="activity-separator">·</span>
                  {lookup.object_type && (
                    <>
                      <span className="object-type-badge">{lookup.object_type}</span>
                      <span className="activity-separator">·</span>
                    </>
                  )}
                  {lookup.object_name && (
                    <>
                      <span className="object-name">{lookup.object_name}</span>
                      <span className="activity-separator">·</span>
                    </>
                  )}
                  {lookup.successful && formatUpdatedData(lookup.updated_data) && (
                    <>
                      <span className="activity-updated-data">
                        {formatUpdatedData(lookup.updated_data)}
                      </span>
                      <span className="activity-separator">·</span>
                    </>
                  )}
                  {!lookup.successful && lookup.reason && (
                    <>
                      <span className="activity-failure-reason">
                        {formatLookupReason(lookup.reason)}
                      </span>
                      <span className="activity-separator">·</span>
                    </>
                  )}
                  {getStatusBadge(lookup.successful, lookup.reason)}
                  {lookup.successful && lookup.lookup_mode === 'global' && (
                    <span className="google-maps-attribution" translate="no">Google Maps</span>
                  )}
                </div>
                <div className="activity-time">
                  {formatTimestamp(lookup.created_at)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default RecentActivity;

